import * as R from 'ramda';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { CSS } from '@dnd-kit/utilities';
import React, { forwardRef } from 'react';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  useSensor,
  DndContext,
  useSensors,
  MouseSensor,
  TouchSensor,
  closestCenter,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// icons
import * as I from '../../../svgs';
// ui
import { Box, Flex, RelativeFlex } from '../../../ui';
//////////////////////////////////////////////////

const dndModifiers = [restrictToHorizontalAxis, restrictToParentElement];

const getPinnedReportId = (report) => `pinned-report-dnd-${G.getGuidFromObject(report)}`;

const PinnedReport = (props) => {
  const {
    guid,
    name,
    type,
    index,
    isActive,
    darkColor,
    innerBorder,
    pinnedReport,
    defaultReport,
    handleSelectReport,
    activeTabBorderColor,
    handlePinOrUnpinReport,
    changeDefaultReportRequest,
  } = props;

  return (
    <Tab
      label={name}
      component={forwardRef((_, ref) => (
        <Box
          py='5px'
          ref={ref}
          ml={G.isZero(index, 0, '5px')}
          borderColor={activeTabBorderColor}
        >
          <Flex px='5px' borderRight={innerBorder} borderColor={darkColor}>
            <Box
              fontSize={12}
              cursor='pointer'
              color={darkColor}
              onClick={() => handleSelectReport(guid)}
              fontWeight={G.ifElse(isActive, 'bold', 'normal')}
            >
              {name}
            </Box>
            {
              isActive &&
              <Flex ml={10}>
                <Flex
                  p='0 3px'
                  cursor='pointer'
                  onClick={() => changeDefaultReportRequest({ reportGuid: guid, reportType: type })}
                >
                  {I.pinNew(G.getThemeByCond(defaultReport, 'colors.black', 'colors.dark.grey'), 8, 10)}
                </Flex>
                <Flex
                  p='3px'
                  cursor='pointer'
                  onClick={() => handlePinOrUnpinReport({ pinnedReport, reportGuid: guid })}
                >
                  {I.closeIcon(darkColor, 8)}
                </Flex>
              </Flex>
            }
          </Flex>
        </Box>
      ))}
    />
  );
};

const SortablePinnedReport = (props) => {
  const {
    index,
    isActive,
    darkColor,
    reportData,
    innerBorder,
    lightGreyColor,
    handleSelectReport,
    activeTabBorderColor,
    handlePinOrUnpinReport,
    changeDefaultReportRequest,
  } = props;

  const sortableId = getPinnedReportId(reportData);

  const {
    listeners,
    transform,
    attributes,
    setNodeRef,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    animateLayoutChanges: () => false,
  });

  const style = {
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    transform: CSS.Translate.toString(transform),
  };

  return (
    <Box
      {...attributes}
      {...listeners}
      style={style}
      ref={setNodeRef}
    >
      <PinnedReport
        {...reportData}
        index={index}
        isActive={isActive}
        darkColor={darkColor}
        innerBorder={innerBorder}
        lightGreyColor={lightGreyColor}
        handleSelectReport={handleSelectReport}
        activeTabBorderColor={activeTabBorderColor}
        handlePinOrUnpinReport={handlePinOrUnpinReport}
        border={G.ifElse(isActive, '1px solid', 'none')}
        changeDefaultReportRequest={changeDefaultReportRequest}
      />
    </Box>
  );
};

export const PinnedReports = (props) => {
  const {
    list,
    zIndex = 1,
    setUsedReport,
    selectedReport,
    handleSelectReport,
    handlePinOrUnpinReport,
    changeDefaultReportRequest,
  } = props;

  const guid = G.getGuidFromObject(selectedReport);
  const darkColor = G.getTheme('colors.black');
  const lightGreyColor = G.getTheme('colors.light.lightGrey');
  const activeTabBorderColor = G.getTheme('colors.light.middleGrey');
  const value = R.indexOf(R.find(R.propEq(guid, GC.FIELD_GUID), list), list);
  const validValue = G.ifElse(R.equals(value, -1), false, value);
  const sortableIds = R.map(getPinnedReportId, list);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }) => {
    const activeId = R.prop('id', active);
    const overId = R.prop('id', over);

    if (R.or(R.isNil(overId), R.equals(activeId, overId))) return;

    const startIndex = R.indexOf(activeId, sortableIds);
    const endIndex = R.indexOf(overId, sortableIds);

    if (R.or(R.equals(startIndex, -1), R.equals(endIndex, -1))) return;

    const newList = G.reorderList(list, startIndex, endIndex);

    handlePinOrUnpinReport(R.map(G.getGuidFromObject, newList));
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      modifiers={dndModifiers}
      collisionDetection={closestCenter}
    >
      <RelativeFlex
        pt='6px'
        zIndex={zIndex}
        justifyContent='space-between'
        bg={G.getTheme('colors.white')}
        borderColor={G.getTheme('colors.lightGrey')}
      >
        {
          G.isNotNilAndNotEmpty(list) &&
          <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
            <Tabs
              value={validValue}
              variant='scrollable'
              scrollButtons='auto'
              indicatorColor='primary'
              style={{ minHeight: '26px' }}
              TabIndicatorProps={{
                sx: {
                  height: 3,
                  backgroundColor: activeTabBorderColor,
                },
              }}
            >
              {
                R.addIndex(R.map)((reportData, index) => {
                  const reportGuid = G.getGuidFromObject(reportData);
                  const isActive = R.equals(guid, reportGuid);

                  const innerBorder = G.ifElse(
                    R.or(isActive, R.equals(index, R.dec(value))),
                    'none',
                    '1px solid',
                  );

                  return (
                    <SortablePinnedReport
                      index={index}
                      key={reportGuid}
                      isActive={isActive}
                      darkColor={darkColor}
                      reportData={reportData}
                      innerBorder={innerBorder}
                      setUsedReport={setUsedReport}
                      lightGreyColor={lightGreyColor}
                      handleSelectReport={handleSelectReport}
                      activeTabBorderColor={activeTabBorderColor}
                      handlePinOrUnpinReport={handlePinOrUnpinReport}
                      changeDefaultReportRequest={changeDefaultReportRequest}
                    />
                  );
                }, list)
              }
            </Tabs>
          </SortableContext>
        }
      </RelativeFlex>
    </DndContext>
  );
};
